import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type ApiQueryPromise, ViewEnum } from '@/types';
import queryKeys from '@/constants/query-keys';
import { getResponseJwtHeader } from '@/utils/web';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useGetSiteConfigs, useGetSiteConfigsWithSession } from './use-site-configs';
import { useSiteStore } from '@/store/site';
import { useAuthToken } from '@/store/auth';
import { useAtomValue } from 'jotai';
import { visitorAtom } from '@/atoms/visitor';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { galaxySidebarSwitchTabIndexAtom, marsSidebarSwitchTabIndexAtom } from '@/atoms/io';
import { getDummySetting } from '@/utils/saba-sports-util';
import { useAppearanceStore } from '@/store/appearance';
import { dummySettings, phSectionSports, virtualSportIds } from '@/settings/sports-settings';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import {
	getForbiddenSport,
	getMarketPriority,
	getMenuMarket,
	isDummySportCategory,
	sortVirtualSports
} from '@/services/settings.service';
import { sportService } from '@/services/sport.service';
import { GalaxySidebarSwitchTabIndex } from '@/types/nav';
import {
	EventMode,
	EventName,
	Group,
	MarketType,
	menuDefinition,
	MenuDefinitionEnum,
	PartialRecord,
	ProvidedMarket,
	ProvidedMatchCount,
	RawMarketData,
	RawMenuData
} from '@/types/menu-setting';
import { attachLeagueGroupIdToHref, getOrderedSubMarketData, isGroupHasSufficientData } from '@/settings/esport-setting';
import { matchSome } from '@nf/utils-common/fpw/monadic';
import { useFilterSabaString } from './use-saba-filter';
import { useAccountInfo } from './account';
import { AccountActStatus } from '@nf/utils-common/constants';
import { useSectionSportsContributor } from './use-section-sports';
import { marketService } from '@/services/market.service';
import { useApiBackendInfo } from './use-api-backend-info';

export type MarketData = RawMarketData & {
	HasLive: boolean;
	Name: string;
	GameId: number;
	Icon: string;
	FirstPriorityFilterableMarket: ProvidedMarket;
	LeagueGroupId?: number;
	GameType?: string;
	Sub?: MarketData[];
	IsComingSoon?: boolean;
};
type ReducedMarketData = {
	sportId: number,
	prioritizedMarket: ProvidedMarket,
	matchCount: number
};

export interface UseOriginMenuDataParams {
	isNeedParlayData?: boolean;
	blockAutoFetch?: boolean;
	both?: boolean;
}


type MenuDataResponse = Array<RawMenuData>;

export interface BothMenuDataResponse {
	n: Array<RawMenuData>;
	p: Array<RawMenuData>;
	BetBuilderPlus: Array<RawMenuData>;
}

type MenuResponse = MenuDataResponse | BothMenuDataResponse;

export const isBothMenuDataResponse = (
	data: MenuResponse
): data is BothMenuDataResponse => {
	return typeof data === 'object' && !Array.isArray(data) && 'n' in data && 'p' in data && 'BetBuilderPlus' in data;
};

export const isMenuDataArray = (
	data: MenuResponse
): data is Array<RawMenuData> => {
	return Array.isArray(data);
};

type RoutePrefix = 'favorites' | 'live' | 'sports' | `world/${EventName}` | 'section';
const predefinedMenuModes = Object.entries(menuDefinition) as [EventMode, EventName][];
export const getEventName = (mode: string): EventName => {
	const modeIndex = predefinedMenuModes.findIndex(keyValuePair => keyValuePair[0] === mode);
	const modeAndName = modeIndex === -1 ? getCorrespondingKeyValuePair(menuDefinition.M0) : predefinedMenuModes[modeIndex];
	return modeAndName[1];
}
export const getCorrespondingKeyValuePair = (mode: string): [EventMode, EventName] => {
	const modeIndex = predefinedMenuModes.findIndex(keyValuePair => keyValuePair[1] === mode);
	return modeIndex === -1 ? getCorrespondingKeyValuePair(menuDefinition.M0) : predefinedMenuModes[modeIndex];
};
type MenuDataByMode = Record<EventMode, MarketData[]>;

export interface UseMenuDataByModeParams {
	isNeedParlayData?: boolean;
	targetView?: ViewEnum;
	both?: boolean;
}

export interface BothMenuDataByMode {
	n: MenuDataByMode;
	p: MenuDataByMode;
	BetBuilderPlus: MenuDataByMode;
}

export interface MenuItems {
	hasLive: boolean;
	icon: string;
	href: string;
	name: string;
	gameId: string;
	matchCount: number;
	subMenu?: MenuItems[];
	isVirtualTournament?: boolean;
}
export interface MarketFilterProperties {
	key: MarketType,
	value: string,
	count: number,
	route: string
} 
export interface MergedMarketFilterProperties {
	n: MarketFilterProperties[],
	p: MarketFilterProperties[],
	betBuilderPlus: MarketFilterProperties[]
}
export interface MenuType {
	modeId: number;
	mode: MenuDefinitionEnum;
	modeType: MenuDefinitionEnum; 
	path: string;
	matchCount: number;
}

export const fetchMenu = async (
	menuAction: string, 
	authToken: string | undefined
): Promise<ApiQueryPromise<MenuResponse>> => {
	const apiDomain = useSiteStore.getState().apiDomain;
	const requestData = {
		isParlay: false,
		both: true,
		defaulteuro: false
	};
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/${menuAction}`),
		{ body: JSON.stringify(requestData) })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

type FetchMenuApiBackendParams = {
	apiBackendUrl: string;
	accessToken: string;
};

/**
 * api-backend menu:
 * - both → POST /Menu/MobileLiteMenu ({ n, p, BetBuilderPlus })
 * - else → POST /Menu/MobileStandardMenu (array by filter)
 */
export const fetchMenuApiBackend = async ({
	apiBackendUrl,
	accessToken
}: FetchMenuApiBackendParams): Promise<ApiQueryPromise<MenuResponse>> => {

	const response = await fetchComposer.postWithBearer(
		cloudUrlParser(`${apiBackendUrl}/Menu/MobileLiteMenu`),
		{ body: JSON.stringify({}) }
	)(accessToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

/**
 * Get all menu data
 * @param params 參數物件
 * @param params.isNeedParlayData 是否為 parlay mode
 * @param params.blockAutoFetch 是否停用 mount 就自動執行 fetch 的功能
 * @param params.both 是否同時獲取 normal 和 parlay 資料
 */

export function useOriginMenuData(params: UseOriginMenuDataParams & { both: true }): {
	menuResponse: BothMenuDataResponse;
	isLoading: boolean;
	isError: boolean;
	error: unknown;
	refetch: () => void;
};
export function useOriginMenuData(params: UseOriginMenuDataParams): {
	menuResponse: Array<RawMenuData>;
	isLoading: boolean;
	isError: boolean;
	error: unknown;
	refetch: () => void;
};

export function useOriginMenuData({
	isNeedParlayData = false,
	blockAutoFetch = false,
	both = false
}: UseOriginMenuDataParams): {
	menuResponse: MenuResponse;
	isLoading: boolean;
	isError: boolean;
	error: unknown;
	refetch: () => void;
} {
	const view = useAppearanceStore(state => state.view);
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const menuAction = isBefore ? 'bfmain/GetContributor' : 'main/GetContributor';
	const visitorInfo = useAtomValue(visitorAtom);
	const useToken = isBefore ? visitorInfo?.AccessToken : authToken;
	const isGalaxy = view === ViewEnum.galaxy;
	const { SiteConfigsData, isFetched } = useGetSiteConfigs();
	const { url: apiBackendUrl, token: apiBackendToken, isReady: isApiBackendReady } = useApiBackendInfo();
	// Flag on but api-backend not ready → fallback GetContributor
	const useApiBackendMenu = !!SiteConfigsData?.EnableMenuApiBackend && isApiBackendReady;
	const hasFetchedSiteConfigsAndApiBackend = !!SiteConfigsData && isFetched && isApiBackendReady;
	const queryConfig = {
		refetchInterval: 30000,
		staleTime: 30000,
		refetchOnWindowFocus: false
	};

	const fetchMenuBySource = (useApiBackendMenu: boolean) => {
		if (useApiBackendMenu) {
			return fetchMenuApiBackend({
				apiBackendUrl,
				accessToken: apiBackendToken
			});
		}
		return fetchMenu(menuAction, useToken);
	};

	const galaxyQuery = useQuery({
		queryKey: [queryKeys.MENU_DATA, menuAction],
		queryFn: () => fetchMenuBySource(useApiBackendMenu),
		...queryConfig,
		enabled: !blockAutoFetch && hasFetchedSiteConfigsAndApiBackend
	});

	const { data, isLoading, isError, error, refetch } = galaxyQuery;

	const menuResponse = useMemo(() => {
		if (!data?.data?.Data) {
			return both ? { n: [], p: [], BetBuilderPlus: [] } : [];
		}

		const responseData = data.data.Data;

		if (both && isBothMenuDataResponse(responseData)) {
			return responseData;
		}

		if (isGalaxy && isBothMenuDataResponse(responseData)) {
			return isNeedParlayData ? responseData?.p : responseData?.n;
		}
		if (isMenuDataArray(responseData)) {
			return responseData;
		}

		if (both) {
			return { n: responseData?.n || [], p: responseData?.p || [], BetBuilderPlus: responseData?.BetBuilderPlus || [] };
		} else {
			return isNeedParlayData ? responseData?.p : responseData?.n || []
		}
	}, [both, data, isGalaxy, isNeedParlayData]);

	return { menuResponse, isLoading, isError, error, refetch };
}

interface CurrentProperties {
	eventMode: EventMode,
	eventName: EventName,
	routePrefix: RoutePrefix,
	sport: number,
	market: string,
	leagueGroupId?: number,
	gameType?: string;
}
export const useCurrentProperties = (): CurrentProperties => {
	const params = useParams();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const leagueGroupId = searchParams.get('lgid');
	const gameType = searchParams.get('gametype');
	const [eventMode, eventName] = getCorrespondingKeyValuePair((!params.mode || Array.isArray(params.mode)) ? menuDefinition.M0 : params.mode);
	const routePrefix: RoutePrefix = pathname.includes('favorites') 
		? 'favorites' 
		: pathname.includes('live') 
			? 'live' 
			: pathname.includes('section') 
				? 'section'
				: pathname.includes('world') 
					? `world/${eventName}` 
					: 'sports';
	const currentProperties: CurrentProperties = {
		eventMode,
		eventName,
		routePrefix,
		sport: Number(`${params.sport}`),
		market: `${params.market}`
	};
	if (leagueGroupId) {
		currentProperties.leagueGroupId = Number(leagueGroupId);
	}
	if (currentProperties.sport === 50) {
		currentProperties.gameType = gameType ?? 'FX';
	}
	return currentProperties;
};
//Calculate matches' by sport
// Outright(999) 不顯示在 Sports Menu
export function useMenuDataByMode(params?: UseMenuDataByModeParams & { both?: false }): MenuDataByMode;
export function useMenuDataByMode(params: UseMenuDataByModeParams & { both: true }): BothMenuDataByMode;

export function useMenuDataByMode(params?: UseMenuDataByModeParams) {
	const {
		isNeedParlayData = false,
		targetView,
		both = false
	} = params || {};
	
	const t = useTranslations('General');
	const { user } = useAccountInfo();
	const { nonSport } = useLicSettings();
	const virtualSoccerList = nonSport?.virtualSoccerList ?? [];
	const { SiteConfigsData } = useGetSiteConfigs();
	const viewFromStore = useAppearanceStore(state => state.view);
	const view = targetView ?? viewFromStore;
	const { menuResponse } = useOriginMenuData({ 
		isNeedParlayData: isNeedParlayData ?? false,
		both: both 
	});
	const [menuDataByMode, setMenuDataByMode] = useState<MenuDataByMode>({} as MenuDataByMode);
	const [bothMenuDataByMode, setBothMenuDataByMode] = useState<BothMenuDataByMode>({
		n: {} as MenuDataByMode,
		p: {} as MenuDataByMode,
		BetBuilderPlus: {} as MenuDataByMode
	});
	const { filterSabaString } = useFilterSabaString();
	const forbiddenSport = getForbiddenSport(view);
	const marketPriority = getMarketPriority(view, isNeedParlayData);
	const markets = Object.keys(marketPriority) as MarketType[];
	const defaultMarktet = view === ViewEnum.galaxy ? 'T' : 'L';
	const isESportEnabled = !forbiddenSport.includes('43');
	const enablePinGoalSports = SiteConfigsData?.EnablePinGoalMenuSports ?? [];
	const isBefore = !useAuthToken();
	const isDeposited = user?.ActStatus === AccountActStatus.Deposited;
	const updateSportsIndex = (currentIndex: number, targetIndex: number, sportsList: any[]) => {
		if (targetIndex !== -1 && currentIndex !== -1) {
			const currentData = sportsList[currentIndex];
			sportsList.splice(currentIndex, 1);
			const targetIndex = sportsList.findIndex((rawMenuData) => rawMenuData.GameId === 50);
			sportsList.splice(targetIndex + 1, 0, currentData);
		}
	}

	useEffect(() => {
		const processArray = (menuDataArray: Array<RawMenuData>): MenuDataByMode => {
			if (!menuDataArray || menuDataArray.length === 0) {
				return {} as MenuDataByMode;
			}
			
			const sortedMenuDataWithoutForbiddenSports = menuDataArray.filter(menuData => !forbiddenSport.includes(menuData.GameId.toString()));
			const soccerIndex = sortedMenuDataWithoutForbiddenSports.findIndex((rawMenuData) => rawMenuData.GameId === 1); //Soccer
			const sabaSoccerIndex = sortedMenuDataWithoutForbiddenSports.findIndex((rawMenuData) => rawMenuData.GameId === 997); //Saba Soccer
			if (soccerIndex !== -1 && sabaSoccerIndex !== -1) {
				sortedMenuDataWithoutForbiddenSports.splice(soccerIndex + 1, 0, sortedMenuDataWithoutForbiddenSports.splice(sabaSoccerIndex, 1)[0]);
			}
			const enablePinGaolCategory = dummySettings.filter(setting => setting.categoryId === 97 && enablePinGoalSports.includes(setting.dummyId)).length > 0;
			if (enablePinGaolCategory && soccerIndex !== -1 && !isNeedParlayData) {
				sortedMenuDataWithoutForbiddenSports.splice(soccerIndex + (sabaSoccerIndex !== -1 ? 2 : 1), 0, {
					GameId: 97,
					Name: filterSabaString(t('lbl_SabaPinG')),
					M0: {
						'Days': {},
						'L': NaN,
					},
				} as RawMenuData)
			} 
			const basketballIndex = sortedMenuDataWithoutForbiddenSports.findIndex((rawMenuData) => rawMenuData.GameId === 2); //BasketBall
			const sabaBasketballIndex = sortedMenuDataWithoutForbiddenSports.findIndex((rawMenuData) => rawMenuData.GameId === 993); //Saba BasketBall
			if (basketballIndex !== -1 && sabaBasketballIndex !== -1) {
				sortedMenuDataWithoutForbiddenSports.splice(basketballIndex + 1, 0, sortedMenuDataWithoutForbiddenSports.splice(sabaBasketballIndex, 1)[0]);
			}
			const cricketIndex = sortedMenuDataWithoutForbiddenSports.findIndex((rawMenuData) => rawMenuData.GameId === 50); //Cricket
			const virtualSoccerIndex = sortedMenuDataWithoutForbiddenSports.findIndex((rawMenuData) => rawMenuData.GameId === 147); //Virtual Soccer
			updateSportsIndex(virtualSoccerIndex, cricketIndex, sortedMenuDataWithoutForbiddenSports);
			
			const eventModes: EventMode[] = Object.keys(menuDefinition) as EventMode[];
			const virtualSportsList: any[] = [];
			let virtualSoccerListMenuEligible = false;
			
			return sortedMenuDataWithoutForbiddenSports
				.reduce((reduced: MenuDataByMode, menuData: RawMenuData, index: number): MenuDataByMode => {
					const { GameId: gameId, Name } = menuData;
					let gameName = gameId !== 997 ? Name : SiteConfigsData?.EnableFilterSaba ? t('lbl_VirtualSoccer') : t('lbl_SabaSoccer')
					if (gameId === 993) {
						gameName = t('lbl_SabaBasketball')
						if (SiteConfigsData?.EnableFilterSaba) gameName = t('lbl_VSabaBasketball')
					}

					const specialGameNames: Record<number, string> = {
						198: 'lbl_198',
						177: 'lbl_177',
						125: 'lbl_125',
						126: 'lbl_126',
						127: 'lbl_127',
					};
					if (specialGameNames[gameId]) {
						gameName = t(specialGameNames[gameId]);
					}

					const isLiteDemoSite = SiteConfigsData?.GetMessageID === '4356000';
					const isLiteDemoGalaxy = isLiteDemoSite && view === ViewEnum.galaxy;
					const liteDemoSportNameMapping: Record<number, string> = {
						1: 'lbl_B2BDemoSiteFootball',           // Soccer → Football
						3: 'lbl_B2BDemoSiteAmericanFootball',   // Football → American Football
						997: 'lbl_B2BDemoSiteSABAFootball',     // SABA Soccer → SABA Football
					};
					if (isLiteDemoGalaxy && liteDemoSportNameMapping[gameId]) {
						gameName = t(liteDemoSportNameMapping[gameId]);
					}

					eventModes.forEach(eventMode => {
						const eventModeData = menuData[eventMode];
						const firstPriorityFilterableMarket = !eventModeData ? undefined : markets.find((filterableMarket: MarketType): boolean => {
							const providedMarket = marketPriority[filterableMarket];
							const marketCount = eventModeData[providedMarket as ProvidedMarket] ?? 0;
							return marketCount > 0 || Number.isNaN(marketCount) || virtualSportIds.includes(gameId.toString())
								|| sportService.isInVirtualSoccerList(gameId, virtualSoccerList)
						});
							
						if (eventModeData && firstPriorityFilterableMarket) {
							const isVirtualSport = gameId === 997 || gameId === 993;
							const marketDataWithGameId: MarketData = {
								...eventModeData,
								HasLive: eventModeData.L > 0 || Number.isNaN(eventModeData.L),
								GameId: gameId,
								Name: gameName,
								Icon: ((isVirtualSport && SiteConfigsData?.EnableFilterSaba) ? gameId === 997 ? '997_special' : '993_special' : `${gameId}`),
								FirstPriorityFilterableMarket: marketPriority[firstPriorityFilterableMarket] ?? defaultMarktet
							};
							if (!isVirtualSport && (eventMode === 'M27' || eventMode === 'M28')) {
								return;
							}
							if (eventMode === 'M29') {
								marketDataWithGameId.Name = SiteConfigsData?.EnableFilterSaba ? t('Menu29') : t('VirtualMenu29');
							}

							if (gameId === 97) {
								marketDataWithGameId.Sub = dummySettings.filter(setting => setting.categoryId === gameId && enablePinGoalSports.includes(setting.dummyId)).map(setting => {
									const sub = {
										...eventModeData,
										HasLive: true,
										GameId: setting.dummyId,
										Name: filterSabaString(t(setting.label)),
										Icon: `${setting.dummyId}`,
										FirstPriorityFilterableMarket: 'L',
										L: 0,
										T: 0
									};
									setting.market.forEach(x => {
										sub[x] = NaN
									});
									return sub as MarketData;
								})
							}

							if (gameId === 43 && isESportEnabled && isGroupHasSufficientData(eventModeData.Group)) {
								const orderedSubMarketData = getOrderedSubMarketData(eventModeData, marketPriority).map(marketData => ({ ...marketData, Name: filterSabaString(t(marketData.Name)) }));
								marketDataWithGameId.Sub = orderedSubMarketData;
								if (orderedSubMarketData.length > 0) {
									if (orderedSubMarketData[0].L > 0) {
										marketDataWithGameId.HasLive = true;
									}
									markets.forEach(market => {
										const providedMarket = (marketPriority[market] as keyof ProvidedMatchCount);
										marketDataWithGameId[providedMarket] = orderedSubMarketData[0][providedMarket];
									});
								}
							}
							if (gameId === 50 && isGroupHasSufficientData(eventModeData.Group) && view === ViewEnum.mars && !isNeedParlayData) {
								const group = eventModeData.Group as Group;
								const groupNames = Object.keys(group);
								marketDataWithGameId.Sub = groupNames.map(groupName => {
									const groupMarketData = group[groupName];
									const firstPriorityFilterableMarket = markets.find((filterableMarket: string): boolean => ((groupMarketData[filterableMarket as ProvidedMarket] ?? 0) > 0));

									return {
										...groupMarketData,
										HasLive: groupMarketData.L > 0,
										GameId: 50,
										Name: t(groupName === 'FX' ? 'menu_FIXED' : 'menu_FANCY'),
										GameType: groupName,
										Icon: '',
										FirstPriorityFilterableMarket: marketPriority[firstPriorityFilterableMarket as MarketType] ?? marketPriority[markets[0]] as keyof ProvidedMatchCount
									};
								})
							}
							if (sportService.isVirtualSports(gameId)) {
								if (sportService.shouldGroupVirtualSoccerMenuFace(gameId, virtualSoccerList)) {
									virtualSoccerListMenuEligible = true;
									return;
								}
								// 如果是 180 且 virtualSoccerList 為空，則跳過
								if (gameId === 180 && sportService.isVirtualSoccerListEmpty(virtualSoccerList)) {
									return;
								}
								virtualSportsList.push({
									HasLive: false,
									E: 0,
									L: 0,
									T: 0,
									O: 0,
									WO: 0,
									Days: {},
									GameId: gameId,
									Icon: `${gameId}`,
									LeagueGroupId: 99,
									Name: gameName,
									FirstPriorityFilterableMarket: defaultMarktet
								});
								if (gameId !== 147 || (isBefore || !user?.VirtuallySoccerSportsMenu)) {
									return;
								}
							}
							if (marketService.isOutrightBySport(gameId) && eventMode !== 'M0') {
								return;
							}
							const modeMenuData = reduced[eventMode];
							if (!modeMenuData) {
								reduced[eventMode] = [marketDataWithGameId];
							} else {
								modeMenuData.push(marketDataWithGameId);
							}
						}
					});
					const hasBetbySports = [user?.LicenseeSiteSetting?.EnableBetbyEsoccer, user?.LicenseeSiteSetting?.EnableBetbyBasketball2K].some(x => Boolean(x));
					if (index === sortedMenuDataWithoutForbiddenSports.length - 1 && (virtualSportsList.length > 0 || hasBetbySports)) {
						const virtualSoccerIndex = virtualSportsList.findIndex((virtualSportsData) => virtualSportsData.GameId === 147); //Virtual Soccer
						updateSportsIndex(virtualSoccerIndex, 0, virtualSportsList);

						if (user?.LicenseeSiteSetting?.EnableBetbyEsoccer) {
							virtualSportsList.unshift({
								HasLive: false,
								E: 0,
								L: 0,
								T: 0,
								O: 0,
								WO: 0,
								Days: {},
								GameId: 116,
								Icon: '116',
								LeagueGroupId: 99,
								Name: t('lbl_BetbyEsoccerPopup03'),
								FirstPriorityFilterableMarket: 'L'
							});
						}

						if (user?.LicenseeSiteSetting?.EnableBetbyBasketball2K) {
							const betbyBasketball2KMenuItem = {
								HasLive: false,
								E: 0,
								L: 0,
								T: 0,
								O: 0,
								WO: 0,
								Days: {},
								GameId: 134,
								Icon: '134',
								LeagueGroupId: 99,
								Name: t('lbl_NBA2K'),
								FirstPriorityFilterableMarket: 'L'
							};
							virtualSportsList.unshift(betbyBasketball2KMenuItem);
							if (isDeposited) {
								let insertAfterIndex = reduced.M0?.findIndex((menuData) => menuData.GameId === 993) ?? -1; // Saba Basketball
								if (insertAfterIndex === -1) {
									insertAfterIndex = reduced.M0?.findIndex((menuData) => menuData.GameId === 2) ?? -1; // Basketball
								}
								if (insertAfterIndex !== -1) {
									reduced.M0?.splice(insertAfterIndex + 1, 0, {
										...betbyBasketball2KMenuItem,
										FirstPriorityFilterableMarket: 'L' as keyof ProvidedMatchCount
									});
								}
							}
						}
						
						if (virtualSoccerListMenuEligible) {
							const kironMenuFaceIds = new Set<number>([180, ...virtualSoccerList]);
							for (let i = virtualSportsList.length - 1; i >= 0; i--) {
								if (kironMenuFaceIds.has(virtualSportsList[i].GameId as number)) {
									virtualSportsList.splice(i, 1);
								}
							}
							virtualSportsList.push({
								HasLive: false,
								E: 0,
								L: 0,
								T: 0,
								O: 0,
								WO: 0,
								Days: {},
								GameId: 180,
								Icon: '180',
								LeagueGroupId: 99,
								Name: t('lbl_180'),
								FirstPriorityFilterableMarket: defaultMarktet
							});
						}

						// vs2 tournament sort
						const sortedVirtualSportsList = sortVirtualSports(virtualSportsList, virtualSoccerList);
						const virtualSportMenuData: MarketData = {
							HasLive: false,
							E: 0,
							L: 0,
							T: 0,
							O: 0,
							WO: 0,
							Days: {},
							GameId: sortedVirtualSportsList[0]['GameId'],
							Name: t('lbl_VirtualSport'),
							Icon: '18x',
							FirstPriorityFilterableMarket: defaultMarktet,
							Sub: sortedVirtualSportsList,
						};
						reduced.M0?.push(virtualSportMenuData);
					}
					return reduced;
				}, {} as MenuDataByMode);
		};

		if (both) {
			if (menuResponse && isBothMenuDataResponse(menuResponse)) {
				const nData = processArray(menuResponse.n || []);
				const pData = processArray(menuResponse.p || []);
				const betBuilderPlusData = processArray(menuResponse.BetBuilderPlus || []);
				
				setBothMenuDataByMode({
					n: nData,
					p: pData,
					BetBuilderPlus: betBuilderPlusData
				});
			}
		} else {
			if (menuResponse && isMenuDataArray(menuResponse)) {
				setMenuDataByMode(processArray(menuResponse));
			}
		}
	}, [
		menuResponse,
		both,
		isNeedParlayData,
		view,
		isDeposited,
		user?.LicenseeSiteSetting?.EnableBetbyEsoccer,
		user?.LicenseeSiteSetting?.EnableBetbyBasketball2K,
		virtualSoccerList.length
	]);
	
	return both ? bothMenuDataByMode : menuDataByMode;
}
const convertMarketDataOfSportIntoSportId_HighestPriorityMarket_MatchCount = (prioritizedMarkets: PartialRecord<MarketType, ProvidedMarket>) => (marketData: MarketData): ReducedMarketData => {
	const marketOrder = Object.keys(prioritizedMarkets) as MarketType[];
	const providedMarketsFromLowestToHighestPriority = marketOrder.slice().reverse();

	return providedMarketsFromLowestToHighestPriority.reduce((reduced, providedMarket) => {
		const menuType = prioritizedMarkets[providedMarket] as keyof ProvidedMatchCount;
		const matchCountOfMarket = marketData[menuType] ?? 0;
		if (matchCountOfMarket > 0) {
			reduced.prioritizedMarket = menuType;
		}
		reduced.matchCount += matchCountOfMarket;
		return reduced;
	}, {
		sportId: marketData.GameId,
		prioritizedMarket: prioritizedMarkets[(providedMarketsFromLowestToHighestPriority[0])] as keyof ProvidedMatchCount,
		matchCount: 0
	});
};
export const useMenuTypeList = (isNeedParlayData?: boolean): { menuTypeList: MenuType[], isLoading: boolean } => {
	const menuDataByMode = useMenuDataByMode({ isNeedParlayData });
	const view = useAppearanceStore(state => state.view);
	const { nonSport } = useLicSettings();
	const marketPriority = getMarketPriority(view, isNeedParlayData);
	const markets = Object.keys(marketPriority) as MarketType[];

	// 檢查是否還在載入中
	const isLoading = !menuDataByMode || Object.keys(menuDataByMode).length === 0;

	const menuTypeList = Object.entries(menuDataByMode).map(pair => {
		const [eventMode, marketDataList] = pair;

		const modeId = Number(eventMode.substring(1));
		const modeKey = `M${modeId}` as EventMode;

		const routeValue = menuDefinition[eventMode as EventMode];
		const isAllSports = eventMode === 'M0';
		const marketOrder: MarketType[] = isAllSports ? markets : ['L', 'T', 'E', 'U', 'O'];
		const { sportId: sportNavigateTo, prioritizedMarket: marketNavigateTo, matchCount: matchCountOfMode } = marketDataList
			.map(convertMarketDataOfSportIntoSportId_HighestPriorityMarket_MatchCount(marketPriority))
			.reduce((reducedMarketDataOfMenuType, reducedMarketDataOfSport) => {
				const { sportId, prioritizedMarket, matchCount } = reducedMarketDataOfSport;
				const marketType = markets.find(market => marketPriority[market] === prioritizedMarket) as MarketType;
				const takeCurrentDataAsNavigationTarget = !isAllSports ? !reducedMarketDataOfMenuType.sportId : marketOrder.indexOf(reducedMarketDataOfMenuType.prioritizedMarket) > marketOrder.indexOf(marketType);
				if (takeCurrentDataAsNavigationTarget) {
					reducedMarketDataOfMenuType.sportId = sportId;
					reducedMarketDataOfMenuType.prioritizedMarket = marketType;
				}
				reducedMarketDataOfMenuType.matchCount += matchCount;
				return reducedMarketDataOfMenuType;
			}, {
				sportId: 0,
				prioritizedMarket: marketOrder[marketOrder.length - 1],
				matchCount: 0
			});
		return {
			modeId,
			mode: routeValue,
			modeType: menuDefinition[modeKey],
			path: generateRoute(
				!isAllSports ? `world/${routeValue}` : (marketNavigateTo === 'L' && view === ViewEnum.galaxy) ? 'live' : 'sports',
				sportNavigateTo,
				marketNavigateTo,
				nonSport?.virtualSoccerList
			),
			matchCount: matchCountOfMode
		};
	});

	return { menuTypeList, isLoading };
};
type ProductsOfMarketData = { n?: MarketData[], p?: MarketData[], betBuilderPlus?: MarketData[] };
export type ProductsOfEventModes = Record<EventMode, ProductsOfMarketData>;

export const mergedMenuByEventMode = (menuDataByMode: BothMenuDataByMode): ProductsOfEventModes => {
	const normalMenuDataByMode = menuDataByMode.n;
	const parlayMenuDataByMode = menuDataByMode.p;
	const betBuilderPlusMenuDataByMode = menuDataByMode.BetBuilderPlus;

	const parlayMarketDataOfMode = Object.entries(parlayMenuDataByMode).reduce((parlayData, pair) => {
		const [eventMode, marketData] = pair;
		parlayData[eventMode as EventMode] = { p: marketData };
		return parlayData;
	}, {} as ProductsOfEventModes);

	const betBuilderPlusDataOfMode = Object.entries(betBuilderPlusMenuDataByMode).reduce((betBuilderPlusData, pair) => {
		const [eventMode, marketData] = pair;
		betBuilderPlusData[eventMode as EventMode] = { betBuilderPlus: marketData };
		return betBuilderPlusData;
	}, {} as ProductsOfEventModes);

	const parlayAndBetBuilderPlusData = Object.entries(betBuilderPlusDataOfMode).reduce((mergedData, pair) => {
		const [eventMode, betBuilderPlusData] = pair;
		const existingData = mergedData[eventMode as EventMode] || {};
		mergedData[eventMode as EventMode] = {
			...existingData,
			...betBuilderPlusData
		};
		return mergedData;
	}, { ...parlayMarketDataOfMode } as ProductsOfEventModes);

	return Object.entries(normalMenuDataByMode).reduce((data, pair) => {
		const [eventMode, marketData] = pair;
		const dataOfMode = data[eventMode as EventMode];
		if (dataOfMode === undefined) {
			data[eventMode as EventMode] = { n: marketData };
		} else {
			dataOfMode.n = marketData;
		}
		return data;
	}, parlayAndBetBuilderPlusData);
};

export const useHomePageProductsOfEventModes = (): ProductsOfEventModes => {
	const menuDataByMode = useMenuDataByMode({ isNeedParlayData: true, both: true });
	return useMemo(() => mergedMenuByEventMode(menuDataByMode), [menuDataByMode]);
};
export interface BBPlusSportInfo {
	sportId: number;
	sportName: string;
	eventMode: EventMode;
	eventName: EventName;
	hasEarly: boolean;
	hasToday: boolean;
	hasLive: boolean;
	earlyCount: number;
	todayCount: number;
	liveCount: number;
}

export interface ParlaySportInfo {
	sportId: number;
	sportName: string;
	eventMode: EventMode;
	eventName: EventName;
	hasEarly: boolean;
	hasToday: boolean;
	hasLive: boolean;
	earlyCount: number;
	todayCount: number;
	liveCount: number;
}

type SportInfo = BBPlusSportInfo | ParlaySportInfo;

const analyzeProductData = (
	productsOfEventModes: ProductsOfEventModes,
	getProductData: (products: ProductsOfMarketData) => MarketData[] | undefined,
	currentEventMode?: EventMode
): Record<number, SportInfo> => {
	const results: Record<number, SportInfo> = {};

	Object.entries(productsOfEventModes).forEach(([eventMode, products]) => {
		// Filter by currentEventMode if provided
		if (currentEventMode && eventMode !== currentEventMode) {
			return;
		}

		const productData = getProductData(products);
		
		if (!productData || productData.length === 0) {
			return;
		}

		const [, eventName] = getCorrespondingKeyValuePair(menuDefinition[eventMode as EventMode]);

		productData.forEach((marketData) => {
			const earlyCount = marketData.E ?? 0;
			const todayCount = marketData.T ?? 0;
			const liveCount = marketData.L ?? 0;

			// Only include sports that have at least one match
			if (earlyCount > 0 || todayCount > 0 || liveCount > 0) {
				results[marketData.GameId] = {
					sportId: marketData.GameId,
					sportName: marketData.Name,
					eventMode: eventMode as EventMode,
					eventName,
					hasEarly: earlyCount > 0,
					hasToday: todayCount > 0,
					hasLive: liveCount > 0,
					earlyCount,
					todayCount,
					liveCount,
				};
			}
		});
	});

	return results;
};

export const analyzeBBPlusData = (productsOfEventModes: ProductsOfEventModes, currentEventMode?: EventMode): Record<number, BBPlusSportInfo> => {
	return analyzeProductData(productsOfEventModes, (products) => products.betBuilderPlus, currentEventMode) as Record<number, BBPlusSportInfo>;
};

export const analyzeParlayData = (productsOfEventModes: ProductsOfEventModes, currentEventMode?: EventMode): Record<number, ParlaySportInfo> => {
	return analyzeProductData(productsOfEventModes, (products) => products.p, currentEventMode) as Record<number, ParlaySportInfo>;
};

export const useBBPlusAnalysis = (): Record<number, BBPlusSportInfo> => {
	const productsOfEventModes = useHomePageProductsOfEventModes();
	const { eventMode: currentEventMode } = useCurrentProperties();
	return useMemo(() => analyzeBBPlusData(productsOfEventModes, currentEventMode), [productsOfEventModes, currentEventMode]);
};

export const useParlayAnalysis = (): Record<number, ParlaySportInfo> => {
	const productsOfEventModes = useHomePageProductsOfEventModes();
	const { eventMode: currentEventMode } = useCurrentProperties();
	return useMemo(() => analyzeParlayData(productsOfEventModes, currentEventMode), [productsOfEventModes, currentEventMode]);
};
// 拆成兩個是因為只有部分元件需要bb plus 的CurrentMenuList
export const useCurrentMenuList = (isNeedParlayData?: boolean) => { 
	const menuDataByMode = useMenuDataByMode({ isNeedParlayData });
	return useCurrentMenuListByMenuData(menuDataByMode);
}

export const useCurrentMenuListForBBPlus = (isParlayMode?: boolean) => { 
	let menuDataByMode = useMenuDataByMode({ isNeedParlayData: isParlayMode });
	const view = useAppearanceStore(state => state.view);
	const bothMenuDataByMode = useMenuDataByMode({ both: true });
	const isFilterBetBuilderPlus = useSiteStore(state => state.isFilterBetBuilderPlus);
	// 因為mars 不需要改變market 的數量 所以只限定galaxy
	if (isFilterBetBuilderPlus && view === ViewEnum.galaxy) {
		menuDataByMode = bothMenuDataByMode.BetBuilderPlus;
	}
	return useCurrentMenuListByMenuData(menuDataByMode);
}

export const useCurrentMenuListByMenuData = (menuDataByMode: MenuDataByMode) => { // get sports of current route
	const { eventMode: currentEventMode, routePrefix: currentRoutePrefix } = useCurrentProperties();
	const [currentData, setCurrentData] = useState<MarketData[]>([]);
	useEffect(() => {
		const dataOfMode = menuDataByMode[currentEventMode];
		const marketDataOfCurrentMode = (!dataOfMode || !Array.isArray(dataOfMode) || !dataOfMode.length) ? [] : dataOfMode;
		setCurrentData(currentRoutePrefix === 'live' ? marketDataOfCurrentMode.filter(sportItem => currentRoutePrefix !== 'live' || sportItem.HasLive || sportItem.GameId === 134) : marketDataOfCurrentMode)
	}, [menuDataByMode, currentEventMode, currentRoutePrefix]);
	return currentData;
}

export const useMenuSportName = (sport: number) => {
	const currentMenuList = useCurrentMenuList();

	return currentMenuList.find(
		element => element.GameId === sport
	)?.Name ?? '';
};

export const generateRoute = (routePrefix: RoutePrefix, sport: number, market: MarketType, virtualSoccerList?: number[]) => {
	// 如果是 180 且 virtualSoccerList 有值，替換成第一個值
	let actualSport = sport;
	if (sport === 180 && sportService.hasVirtualSoccerList(virtualSoccerList)) {
		actualSport = sportService.getFirstVirtualSoccerSport(virtualSoccerList)!;
	}
	
	if (sportService.isBetbyVirtualSports(actualSport)) {
		return `/${routePrefix}/${actualSport}`;
	}

	const formattedMarket = market === 'O' ? 'outright' : market.toLowerCase();
	return routePrefix === 'favorites'
		? `/${routePrefix}/${formattedMarket}`
		: routePrefix === 'section'
			? `/${routePrefix}/ph/${formattedMarket}`
			: routePrefix === 'live'
				? `/${routePrefix}/${actualSport}`
				: `/${routePrefix}/${actualSport}/${formattedMarket}`;
};

// 添加接口定义
export interface SectionSportsData {
	days?: { [key: string]: number };
	marketType?: {
		l?: number;
		t?: number;
		e?: number;
		o?: number;
	};
}

// 這是舊版filter 因為只有部分元件需要bb plus filter 所以拆成兩個 
export const useCurrentMarketFilters = (marketSequence: MarketType[]): MarketFilterProperties[] => {
	const [isParlayMode] = useSiteStore(state => [state.isParlayMode]);
	const currentMenuList = useCurrentMenuList(isParlayMode);
	return useCurrentMarketFiltersByMenuList(marketSequence, currentMenuList);
}
// 如果site是filter bb plus 且需要bb plus資料才使用useCurrentMarketFiltersForBBPlus
export const useCurrentMarketFiltersForBBPlus = (marketSequence: MarketType[]): MarketFilterProperties[] => {
	const [isParlayMode] = useSiteStore(state => [state.isParlayMode]);
	const view = useAppearanceStore(state => state.view);
	let menuDataByMode = useMenuDataByMode({ isNeedParlayData: isParlayMode });
	const bothMenuDataByMode = useMenuDataByMode({ both: true });
	const isFilterBetBuilderPlus = useSiteStore(state => state.isFilterBetBuilderPlus);
	// 因為mars 不需要改變market 的數量 所以只限定galaxy
	if (isFilterBetBuilderPlus && view === ViewEnum.galaxy) {
		menuDataByMode = bothMenuDataByMode.BetBuilderPlus;
	}
	const currentMenuList = useCurrentMenuListByMenuData(menuDataByMode);
	return useCurrentMarketFiltersByMenuList(marketSequence, currentMenuList);
} 


export const useCurrentMarketFiltersByMenuList = (marketSequence: MarketType[], currentMenuList: MarketData[]): MarketFilterProperties[] => {
	const { routePrefix: currentRoutePrefix, sport: currentSport, leagueGroupId, gameType } = useCurrentProperties();
	const [currentMarketData, setCurrentMarketData] = useState<MarketData | undefined>(undefined);
	const view = useAppearanceStore(state => state.view);
	const { nonSport } = useLicSettings();
	const marketPriority = getMarketPriority(view);
	const markets = Object.keys(marketPriority) as MarketType[];
	const marketsHasMatchCount = markets.map(market => marketPriority[market]) as (keyof ProvidedMatchCount)[];
	const gameTypeMarketData = gameType ? ((currentMarketData?.Group ?? {}) as Group)[gameType] : null;
	const { SiteConfigsData } = useGetSiteConfigs();
	const { SiteConfigsWithSessionData } = useGetSiteConfigsWithSession();
	const { data: sectionData } = useSectionSportsContributor(
		SiteConfigsData?.DirectApiUrl ?? '', 
		phSectionSports, 
		'PHL'
	);

	useEffect(() => {
		if (currentRoutePrefix === 'favorites' || currentRoutePrefix === 'section') {
			if (SiteConfigsWithSessionData?.EnablePhPortal && currentRoutePrefix === 'section') {
				try {
					if (sectionData?.data) {
						const data = sectionData?.data as SectionSportsData;

						// 转换日期格式
						const convertedDays = data.days 
							? Object.entries(data.days).reduce((acc, [date, count]) => {
								const shortDate = date.substring(2);
								acc[shortDate] = count;
								return acc;
							}, {} as { [key: string]: number })
							: {};

						const marketData = {
							L: data.marketType?.l || 0,
							T: data.marketType?.t || 0,
							E: data.marketType?.e || 0,
							O: data.marketType?.o || 0,
							Days: convertedDays,
							HasLive: Boolean(data.marketType?.l),
							Name: 'ph',  
							GameId: 0, 
							Icon: '',  
							FirstPriorityFilterableMarket: 'L' as ProvidedMarket
						} as MarketData;

						setCurrentMarketData(marketData);
					}
				} catch (error) {
					console.error('Error fetching section data:', error);
				}
			} else {
				// 原有的 favorites 逻辑
				setCurrentMarketData(
					currentMenuList.reduce((matchCountOfMarkets, marketDataOfEachSport) => {
						marketsHasMatchCount.forEach(filterableMarket => {
							const matchCount = marketDataOfEachSport[filterableMarket];
							if (matchCount) {
								matchCountOfMarkets[filterableMarket] = (matchCountOfMarkets[filterableMarket] || 0) + matchCount;
							}
						});
						return matchCountOfMarkets;
					}, {} as MarketData)
				);
			}
		} else {
			// 原有的其他路由逻辑
			const dummySetting = getDummySetting(currentSport);
			const marketDataOfSport = currentMenuList.find(marketData => 
				marketData.GameId === (dummySetting?.categoryId ?? currentSport)
			);
				
			if (dummySetting && marketDataOfSport?.Sub) {
				setCurrentMarketData(marketDataOfSport.Sub.find(x => x.GameId === dummySetting.dummyId));
				return;
			}
				
			setCurrentMarketData(!leagueGroupId 
				? marketDataOfSport 
				: marketDataOfSport?.Sub?.find(marketDataOfSub => marketDataOfSub.LeagueGroupId === leagueGroupId)
			);
		}
	}, [
		currentMenuList, 
		currentSport, 
		currentRoutePrefix, 
		leagueGroupId, 
		SiteConfigsData?.DirectApiUrl,
		SiteConfigsWithSessionData?.EnablePhPortal,
		sectionData
	]);

	return !currentMarketData ? []
		: marketSequence
			.filter(market => {
				const isFilterableMarket = markets.includes(market);
				const marketCount = currentMarketData[market === 'U' ? 'WO' : market];
				const hasMatch = marketCount > 0 || Number.isNaN(marketCount);
				return isFilterableMarket && hasMatch;
			})
			.map(market => {
				const providedMarket = marketPriority[market] as keyof ProvidedMatchCount;
				const count = gameTypeMarketData ? gameTypeMarketData[providedMarket] : currentMarketData[providedMarket];
				return {
					key: market,
					value: market === 'O' ? 'outright' : market.toLowerCase(),
					count: count,
					route: generateRoute(currentRoutePrefix, currentSport, market, nonSport?.virtualSoccerList) +
						(!leagueGroupId ? '' : `?lgid=${leagueGroupId}`) +
						(!gameType ? '' : `?gametype=${gameType}`)
				}
			});
};

//Filter no matches' sports
export const useSportMenuItemGetter = (isNeedParlayData?: boolean, targetView?: ViewEnum) => {
	const viewFromStore = useAppearanceStore(state => state.view);
	const view = targetView ?? viewFromStore;
	const sidebarSwitchTabIndex = useAtomValue(view === ViewEnum.galaxy ? galaxySidebarSwitchTabIndexAtom : marsSidebarSwitchTabIndexAtom);
	const menuDataByMode = useMenuDataByMode({ isNeedParlayData, targetView: view });
	const marketPriority = getMenuMarket(view);
	const markets = Object.keys(marketPriority) as MarketType[];
	const providedMarkets = markets.map(market => marketPriority[market]) as (keyof ProvidedMatchCount)[];
	const defultMarket = view === ViewEnum.galaxy ? 'T' : 'L';
	const { nonSport } = useLicSettings();

	const getSportMenuItemsOfMode = (mode: EventMode): MenuItems[] => {
		const [, eventName] = getCorrespondingKeyValuePair(menuDefinition[mode]);
		const dataOfMode: MarketData[] = menuDataByMode[mode] ?? [];
		const isCurrentlyLive = mode === 'M0' && (view === ViewEnum.galaxy && sidebarSwitchTabIndex === GalaxySidebarSwitchTabIndex.Live);

		const convertMarketDataToMenuItem = (marketData: MarketData, isSubItem: boolean): MenuItems => {
			const marketType = markets.find(market => marketPriority[market] === marketData.FirstPriorityFilterableMarket);
			const isSportsBetbyBasketball2KItem = !isSubItem && marketData.GameId === 134;

			const href = generateRoute(
				mode !== 'M0'
					? `world/${eventName}`
					: isCurrentlyLive && (!sportService.isAnyVirtualSportsProduct(marketData.GameId) || isSportsBetbyBasketball2KItem)
						? 'live'
						: 'sports',
				marketData.GameId,
				marketType ?? defultMarket,
				nonSport?.virtualSoccerList
			);

			return {
				hasLive: marketData.HasLive,
				name: marketData.Name,
				icon: marketData.Icon,
				href: href,
				gameId: String(marketData.GameId),
				matchCount: providedMarkets
					.filter(market => isCurrentlyLive ? market === 'L' : true)
					.reduce((sum, market) => {
						const matchCountOfMarket = marketData[market];
						if (matchCountOfMarket && typeof marketData[market] === 'number') {
							sum += marketData[market];
						}
						return sum;
					}, 0)
			};
		};

		return dataOfMode
			.filter((marketData: MarketData): boolean => isCurrentlyLive ? (sportService.isAnyVirtualSportsProduct(marketData.GameId) ? true : marketData.HasLive) : true)
			.map((marketData: MarketData): MenuItems => {
				const menuItem = convertMarketDataToMenuItem(marketData, false);
				if (marketData.Sub) {
					menuItem.subMenu = marketData.Sub.filter(marketData => !isCurrentlyLive || sportService.isAnyVirtualSportsProduct(marketData.GameId) ? true : marketData.HasLive || marketData.L > 0)
						.map(marketData => {
							const menuItem = convertMarketDataToMenuItem(marketData, true);
							return menuItem.gameId === '43' ? attachLeagueGroupIdToHref(menuItem, marketData) :
								menuItem.gameId === '50' ? attachGameTypeToHref(menuItem, marketData) : menuItem;
						});
					if (menuItem.gameId === '97') {
						menuItem.href = menuItem.subMenu[0].href;
					}
				}
				return menuItem;
			});
	};

	return {
		getSportMenuItemsOfMode
	}

};

export const useCurrentSportMenuItems = (isNeedParlayData?: boolean) => {
	const [menuItems, setMenuItems] = useState(Array<MenuItems>);
	const currentMenuList = useCurrentMenuList(isNeedParlayData);
	const currentRoutePrefix = useCurrentProperties().routePrefix;
	const view = useAppearanceStore(state => state.view);
	const { nonSport } = useLicSettings();
	const marketPriority = getMarketPriority(view, isNeedParlayData);
	const markets = Object.keys(marketPriority) as MarketType[];
	const providedMarkets = markets.map(market => marketPriority[market]) as (keyof ProvidedMatchCount)[];

	useEffect(() => {
		setMenuItems(currentMenuList
			.map(sportItem => {
				const marketType = markets.find(market => marketPriority[market] === sportItem.FirstPriorityFilterableMarket) as MarketType;
				isDummySportCategory(sportItem.GameId)
				const href = matchSome(sportItem.GameId)
					.on(isDummySportCategory, () => generateRoute(currentRoutePrefix, sportItem.Sub?.[0].GameId ?? sportItem.GameId, marketType, nonSport?.virtualSoccerList))
					.otherwise(() => generateRoute(currentRoutePrefix, sportItem.GameId, marketType, nonSport?.virtualSoccerList) + (sportItem.GameId === 50 && view === ViewEnum.mars ? `?gametype=${(sportItem.Sub ?? [])[0]?.GameType}` : ''))
				return {
					hasLive: sportItem.HasLive,
					name: sportItem.Name,
					icon: sportItem.Icon,
					href: href,
					gameId: String(sportItem.GameId),
					matchCount: providedMarkets.reduce((sum, providedMarket) => {
						const matchCountOfMarket = sportItem[providedMarket];
						if (matchCountOfMarket && typeof sportItem[providedMarket] === 'number') {
							sum += sportItem[providedMarket];
						}
						return sum;
					}, 0)
				}
			}));
	}, [currentMenuList, currentRoutePrefix, nonSport?.virtualSoccerList]);

	return menuItems;
};

export const useCurrentSubMenuItems = (isNeedParlayData?: boolean): Array<MenuItems> => {
	const { eventMode, sport: currentSport, routePrefix: currentRoutePrefix, market } = useCurrentProperties();
	const sport = getDummySetting(currentSport)?.categoryId ?? currentSport;
	const menuDataByMode = useMenuDataByMode({ isNeedParlayData });
	const sportsOfCurrentEventMode = menuDataByMode[eventMode];
	const view = useAppearanceStore(state => state.view);
	const { nonSport } = useLicSettings();
	const marketPriority = getMarketPriority(view, isNeedParlayData);
	const markets = Object.keys(marketPriority) as MarketType[];
	const providedMarkets = markets.map(market => marketPriority[market]) as (keyof ProvidedMatchCount)[];
	const defaultMarket = view === ViewEnum.galaxy ? 'T' : 'L';

	if (!sportsOfCurrentEventMode) {
		return [];
	}

	let indexOfCurrentSport = -1;
	for (let i = sportsOfCurrentEventMode.length - 1; i >= 0; i--) {
		if (matchSome(sportsOfCurrentEventMode[i].GameId)
			.on(
				() => sportService.isAnyVirtualSportsProduct(sport), 
				() => true
			)
			.otherwise(() => sportsOfCurrentEventMode[i].GameId === sport)
		) {
			indexOfCurrentSport = i;
			break;
		}
	}

	return (indexOfCurrentSport === -1 || !sportsOfCurrentEventMode[indexOfCurrentSport].Sub) ? []
		: (sportsOfCurrentEventMode[indexOfCurrentSport].Sub || [])
			.filter(marketData => currentRoutePrefix !== 'live' || marketData.HasLive || marketData.L > 0)
			.map(marketData => {
				const currentMarket = market as MarketType;
				const currentProvidedMarket = marketPriority[currentMarket];
				const marketType = marketData[currentProvidedMarket ?? defaultMarket] > 0 ? (currentProvidedMarket ? currentMarket : defaultMarket) : markets.find(market => marketPriority[market] === marketData.FirstPriorityFilterableMarket);

				const menuItem = {
					hasLive: marketData.HasLive,
					name: marketData.Name,
					icon: marketData.Icon,
					href: generateRoute(currentRoutePrefix, marketData.GameId, marketType ?? defaultMarket, nonSport?.virtualSoccerList),
					gameId: `${marketData.GameId}`,
					matchCount: currentRoutePrefix === 'live' ? marketData.L : providedMarkets.reduce((sum, providedMarket) => {
						const matchCountOfMarket = marketData[providedMarket];
						if (matchCountOfMarket && typeof marketData[providedMarket] === 'number') {
							sum += marketData[providedMarket];
						}
						return sum;
					}, 0)
				};
				return marketData.GameId === 43 ? attachLeagueGroupIdToHref(menuItem, marketData) :
					marketData.GameId === 50 ? attachGameTypeToHref(menuItem, marketData) : menuItem;
			})
};

export const useSwitchParlayRoute = (isToParlay: boolean) => {
	const { menuResponse } = useOriginMenuData({ isNeedParlayData: isToParlay });
	const { sport, market } = useCurrentProperties();
	const view = useAppearanceStore(state => state.view);
	const marketPriority = getMarketPriority(view, isToParlay);
	const markets = Object.keys(marketPriority) as MarketType[];

	if (!isMenuDataArray(menuResponse)) {
		return { sport: 1, market: 'l' };
	}

	const menuData = menuResponse.find((menuData) => menuData.GameId === sport);

	if (menuData && !sportService.isAnyVirtualSportsProduct(sport)) {
		const marketSort = rearrangeArray(markets, markets.indexOf(market.toUpperCase() as MarketType));
		const targetMarket = marketSort.find(market => menuData.M0[marketPriority[market] as keyof ProvidedMatchCount] > 0) as MarketType;
		return { sport, market: (targetMarket ?? 'D').toLowerCase() };
	}

	const defaultMenuData = menuResponse[0];
	if (defaultMenuData) {
		const targetMarket = markets.find(market => defaultMenuData?.M0[marketPriority[market] as keyof ProvidedMatchCount] > 0) as MarketType;

		return { sport: defaultMenuData.GameId, market: (targetMarket ?? 'l').toLowerCase() };
	}

	return { sport: 1, market: 'l' };

	/*const rearrangeSportSort = rearrangeArray(sportSort, index);
	const targetSport = rearrangeSportSort.find(sport => {
		const rawMenuData = targetMenuData.find(rawMenuData => rawMenuData.GameId === sport);

		if (!rawMenuData) {
			return false;
		}
	
		return rawMenuData.M0[marketPriority[market.toUpperCase() as MarketType] as keyof ProvidedMatchCount] > 0
	}) as number;
	
	return { sport: targetSport, market }*/
};

const rearrangeArray = <T>(array: Array<T>, index: number) => {
	return array.slice(index).concat(array.slice(0, index));
}

const attachGameTypeToHref = (menuItem: MenuItems, marketData: MarketData): MenuItems => {
	return !marketData.GameType ? menuItem : { ...menuItem, href: `${menuItem.href}?gametype=${marketData.GameType}` }
}
